import { useState, useEffect } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import classes from './assessment.module.scss';
import Dropdown from '../controls/Dropdown';
import DatePicker from '../controls/DatePicker';
import MultiSelect from '../controls/MultiSelect';
import MultiSelectModel from '../../models/MultiSelectModel';
import { commonServices } from '../../services/commonService';
import ModalFooter from '../controls/ModalFooter';
import {
    AssessmentStatus,
    DialogAction,
    UserRoleEnum,
    UserStatus,
} from '../../models/Enums';
import {
    CreateAssessmentModel,
    UpdateAssessmentRequestModel,
} from '../../models/AssessmentModel';
import { assessmentService } from '../../services/assessmentService';
import { userService } from '../../services/userService';
import { CurrentUserModel } from '../../models/ProfileModel';
import { authService } from '../../services/authService';
import { useDispatch } from 'react-redux';
import { updateStrategySelection } from '../../redux/ducks/scopedefinition';
import { StrategySelectionModel } from '../../models/ScopeDefinitionModel';
import { UtilityHelper } from '../../utils/UtilityHelper';
import { Constants } from '../../models/Constants';

type AddAssessmentProps = {
    assessmentId?: string;
    onDialogClose: (
        action: DialogAction,
        assessmentId?: string,
        country?: string
    ) => void;
    assessmentStatus?: number;
};

const AddAssessment = (props: AddAssessmentProps) => {
    const { assessmentId, onDialogClose, assessmentStatus } = props;
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [countries, setCountries] = useState<Array<MultiSelectModel>>([]);
    const [assessment, setAssessment] = useState<CreateAssessmentModel>(
        CreateAssessmentModel.init()
    );
    const [users, setUsers] = useState<Array<MultiSelectModel>>([]);
    const [managers, setManagers] = useState<Array<MultiSelectModel>>([]);
    const [error, setError] = useState<any>({});
    const [isLoadingManagers, setIsLoadingManagers] = useState(false);
    const [changeTimeout, setChangeTimeout] = useState<NodeJS.Timeout | null>(null);
    const userInfo: CurrentUserModel = authService.getCurrentUser();

    const [canChangeManager, setCanChangeManager] = useState<boolean | null>(
        false
    );

    const getSelectedUserType = () => {
        try {
            const selectedUserTypeStr = sessionStorage.getItem(Constants.SessionStorageKey.SELECTED_USER_TYPE);
            return selectedUserTypeStr ? Number(selectedUserTypeStr) : userInfo.userType;
        } catch (error) {
            return userInfo.userType;
        }
    };

    const selectedUserType = getSelectedUserType();

    useEffect(() => {
        if (managers.length > 0 && !assessmentId && assessment.countryId) {
            handleManagerSelection(assessment.countryId, managers);
        }
    }, [managers, assessmentId]);

    useEffect(() => {
        return () => {
            if (changeTimeout) {
                clearTimeout(changeTimeout);
            }
        };
    }, [changeTimeout]);

    useEffect(() => {
        commonServices
            .getCountriesForCurrentUser()
            .then((records: Array<MultiSelectModel>) => {
                setCountries(records);
                if (assessmentId) {
                    assessmentService
                        .getAssessmentById(assessmentId)
                        .then((response: CreateAssessmentModel) => {
                            setCanChangeManager(response?.permissions?.canChangeManager);

                            const startDate = new Date(
                                response.startDate.toString().split('T')[0]
                            );
                            response.startDate = startDate;

                            const endDate = new Date(
                                response.endDate.toString().split('T')[0]
                            );
                            response.endDate = endDate;

                            setAssessment(response);
                            bindUsers(response.countryId);
                            bindManagers(response.countryId);
                        });
                }
            });
    }, []);

    const onChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
        if (changeTimeout) {
            clearTimeout(changeTimeout);
        }

        if (evt.target.name === 'countryId') {
            const newTimeout = setTimeout(() => {
                bindUsers(evt.target.value);
                bindManagers(evt.target.value);
            }, 100);

            setChangeTimeout(newTimeout);

            setAssessment({
                ...assessment,
                [evt.target.name]: evt.target.value,
            });
        } else if (evt.target.name === 'manager') {
            onManagerChange(evt.target.name, evt.target.value);
        } else {
            setAssessment({
                ...assessment,
                [evt.target.name]: evt.target.value,
            });
        }

        removeControlValidationOnChange(evt.target.value, evt.target.name);
    };

    const onManagerChange = (field: string, value: string) => {
        const updatedUsers = [...managers];
        const updatedAssessment = { ...assessment };
        updatedAssessment.editors = updatedAssessment.editors.filter(
            (editor: string) => editor !== value
        );
        updatedAssessment.reviewers = updatedAssessment.reviewers.filter(
            (reviewer: string) => reviewer !== value
        );

        setAssessment({
            ...updatedAssessment,
            [field]: value,
        });
    };

    const bindManagers = async (countryId: string) => {
        if (countryId) {
            setIsLoadingManagers(true);
            try {
                const result = await userService.getManagersByCountry(countryId);
                const managers = result.map((data: any) => {
                    return new MultiSelectModel(data.id, data.name);
                });

                setTimeout(() => {
                    setManagers(managers);
                    setIsLoadingManagers(false);
                }, 50);

            } catch (error) {
                console.error('Failed to fetch managers:', error);
                setManagers([]);
                setIsLoadingManagers(false);
            }
        } else {
            setManagers([]);
            setIsLoadingManagers(false);
            setAssessment(prevAssessment => ({
                ...prevAssessment,
                manager: '',
                editors: [],
                reviewers: []
            }));
        }
    };

    const handleManagerSelection = (countryId: string, managersList: Array<MultiSelectModel>) => {
        if (isLoadingManagers) return;

        const currentUserInManagerList = managersList.find(
            (manager: MultiSelectModel) => manager.id === userInfo.userId
        );

        if (currentUserInManagerList) {
            setAssessment(prevAssessment => ({
                ...prevAssessment,
                manager: userInfo.userId,
                countryId: countryId,
            }));
        } else {
            setAssessment(prevAssessment => ({
                ...prevAssessment,
                countryId: countryId,
                manager: '',
            }));
        }
    };

    const bindUsers = (countryId: string) => {
        if (countryId) {
            userService.getUsersByCountry(countryId).then((result: any) => {
                const users = result
                    .filter((user: any) => user.userType == UserRoleEnum.Viewer)
                    .map((data: any) => {
                        return new MultiSelectModel(data.id, data.name);
                    });

                setUsers(users);
            });
        } else {
            setUsers([]);
        }
    };

    const onMultipSelectChange = (
        selectedValues: Array<MultiSelectModel>,
        field: string
    ) => {
        setAssessment({
            ...assessment,
            [field]: selectedValues.map(({ id }) => id),
        });
        removeControlValidationOnChange(selectedValues, field);
    };

    const onDateChange = (date: Date | null, field: string) => {
        const dateValue = date ? new Date(date) : null;
        setAssessment({
            ...assessment,
            [field]: dateValue,
        });
        removeControlValidationOnChange(date?.toString() as string, field);
    };

    const createUpdateAssessment = () => {
        if (!isFormValid()) {
            setError(validate());
            return;
        }

        if (validateSameUserAssignedAsDifferentRoles()) {
            return;
        }

        const country =
            countries.find(
                (country: MultiSelectModel) => country.id === assessment.countryId
            )?.text || '';

        if (!assessmentId) {
            assessmentService.createAssessment(assessment).then(assessmentId => {
                if (assessmentId) {
                    onDialogClose(DialogAction.Add, assessmentId as string, country);
                }

                UtilityHelper.onEventAnalytics(
                    'Assessment',
                    'Add new assessment',
                    'Add assessment'
                );
            });
        } else {
            const { countryId, startDate, endDate, manager, reviewers, editors } =
                assessment;
            const updateAssessment: UpdateAssessmentRequestModel =
                new UpdateAssessmentRequestModel(
                    assessmentId,
                    countryId,
                    startDate,
                    endDate,
                    manager,
                    editors,
                    reviewers
                );

            assessmentService
                .updateAssessment(updateAssessment)
                .then(assessmentId => {
                    if (assessmentId) {
                        onDialogClose(DialogAction.Edit, assessmentId as string);
                    }

                    UtilityHelper.onEventAnalytics(
                        'Assessment',
                        'Update assessment',
                        'Update assessment'
                    );
                });
        }

        dispatch(updateStrategySelection(StrategySelectionModel.init()));
    };

    const removeControlValidationOnChange = (
        value: string | Array<MultiSelectModel>,
        field: string
    ) => {
        if (value?.length > 0) {
            const formError = { ...error };
            delete formError[field];
            setError({ ...formError });
        } else {
            setError({ ...error, [field]: 'Field is mandatory' });
        }
    };
    const isFormValid = () => Object.keys(validate()).length === 0;

    const validateSameUserAssignedAsDifferentRoles = () => {
        const alreadyAssigned =
            assessment.editors.filter(
                (editor: string) =>
                    assessment.reviewers.includes(editor) ||
                    assessment.reviewers.includes(assessment.manager) ||
                    assessment.editors.includes(assessment.manager)
            ).length > 0;

        if (alreadyAssigned) {
            setError({
                ...error,
                userAlreadyAssigned: t('Assessment.SameUserDifferentRole'),
            });
        } else {
            const updatedError = { ...error };
            delete updatedError['userAlreadyAssigned'];
            setError(updatedError);
        }

        return alreadyAssigned;
    };

    const validate = () => {
        let error: any = {};
        const ignoreRequiredFields = ['editors', 'reviewers'];
        Object.keys(assessment)
            .filter((key: string) => !ignoreRequiredFields.includes(key))
            .map((key: string) => {
                const assessmentModel = assessment as any;
                const value = assessmentModel[key];

                const isEmpty =
                    key === 'startDate' || key === 'endDate'
                        ? !value || (value instanceof Date && isNaN(value.getTime()))
                        : !value ||
                        (typeof value === 'string' && value.length === 0) ||
                        (Array.isArray(value) && value.length === 0);

                if (isEmpty) {
                    error = { ...error, [key]: t('Errors.MandatoryField') };
                } else {
                    if (key == 'startDate' || key == 'endDate') {
                        const dateValue = value;
                        const checkDate: boolean =
                            dateValue instanceof Date
                                ? UtilityHelper.isValidDate(dateValue)
                                : UtilityHelper.isValidDate(new Date(dateValue));
                        if (!checkDate) {
                            error = { ...error, [key]: t('Errors.InvalidDate') };
                        } else {
                            delete error[key];
                        }
                    } else {
                        delete error[key];
                    }
                }
                return error;
            });

        if (assessment.startDate && assessment.endDate) {
            const startDate =
                assessment.startDate instanceof Date
                    ? assessment.startDate
                    : new Date(assessment.startDate);
            const endDate =
                assessment.endDate instanceof Date
                    ? assessment.endDate
                    : new Date(assessment.endDate);

            const startDateOnly = new Date(
                startDate.getFullYear(),
                startDate.getMonth(),
                startDate.getDate()
            );
            const endDateOnly = new Date(
                endDate.getFullYear(),
                endDate.getMonth(),
                endDate.getDate()
            );

            if (startDateOnly > endDateOnly) {
                error = {
                    ...error,
                    endDate:
                        t('Errors.InvalidDateRange') || 'End date must be after start date',
                };
            }
        }

        return error;
    };

    return (
        <>
            <div className={classNames(classes.formWrapper)}>
                <div className='row mb-3'>
                    <div className='col-xs-12 col-md-6'>
                        <Dropdown
                            id='countryId'
                            name='countryId'
                            disabled={!!assessmentId}
                            value={assessment.countryId}
                            options={countries}
                            label={t('UserManagement.GridColumn.Country')}
                            error={error['countryId'] ? true : false}
                            helperText={error['countryId']}
                            onChange={onChange}
                            InputLabelProps={{ required: true, shrink: true }}
                        />
                    </div>
                </div>

                <div className='row mb-3'>
                    <div className='col-xs-12 col-md-6'>
                        <DatePicker
                            value={assessment.startDate}
                            label={t('Common.StartDate')}
                            error={error['startDate'] ? true : false}
                            helperText={error['startDate']}
                            onAccept={(date: Date | null) => onDateChange(date, 'startDate')}
                            onChange={(date: Date | null) => onDateChange(date, 'startDate')}
                            InputLabelProps={{ required: true, shrink: true }}
                            openTo='year'
                            views={['year', 'month', 'day']}
                            disabled={assessmentStatus == AssessmentStatus.Published}
                            placeholder={t('Common.YearMonthDate')}
                            onKeyDown={e => {
                                e.preventDefault();
                            }}
                        />
                    </div>
                    <div className='col-xs-12 col-md-6'>
                        <DatePicker
                            value={assessment.endDate}
                            label={t('Assessment.ExpectedEndDate')}
                            minDate={assessment.startDate}
                            error={error['endDate'] ? true : false}
                            helperText={error['endDate']}
                            onAccept={(date: Date | null) => onDateChange(date, 'endDate')}
                            onChange={(date: Date | null) => onDateChange(date, 'endDate')}
                            InputLabelProps={{ required: true, shrink: true }}
                            openTo='year'
                            views={['year', 'month', 'day']}
                            disabled={assessmentStatus == AssessmentStatus.Published}
                            placeholder={t('Common.YearMonthDate')}
                            onKeyDown={e => {
                                e.preventDefault();
                            }}
                        />
                    </div>
                </div>

                <h6 className='mb-4 modal-content-head'>
                    {t('Assessment.AssignUserRole')}
                </h6>

                <div className='row mb-3'>
                    <div className='col-xs-12 col-md-6'>
                        <Dropdown
                            id='manager'
                            name='manager'
                            options={managers}
                            value={assessment.manager}
                            label={t('Assessment.Manager')}
                            error={error['manager'] ? true : false}
                            helperText={error['manager']}
                            onChange={onChange}
                            disabled={assessmentStatus == AssessmentStatus.Published}
                            InputLabelProps={{ required: true, shrink: true }}
                        />
                    </div>
                </div>

                <div className='row mb-3'>
                    <div className='col-xs-12 col-md-6'>
                        <MultiSelect
                            id='editors'
                            values={users.filter((user: MultiSelectModel) => {
                                return assessment.editors.includes(user.id);
                            })}
                            label={t('Assessment.Editors')}
                            options={users}
                            onUpdate={(selectedOptions: MultiSelectModel[]) => {
                                onMultipSelectChange(selectedOptions, 'editors');
                            }}
                        />
                        <small className='fw-lighter'>
                            {t('Assessment.EditorsTooltip')}
                        </small>
                    </div>
                    <div className='col-xs-12 col-md-6'>
                        <MultiSelect
                            id='reviewers'
                            values={users.filter((user: MultiSelectModel) => {
                                return assessment.reviewers.includes(user.id);
                            })}
                            label={t('Assessment.Reviewers')}
                            options={users}
                            onUpdate={(selectedOptions: MultiSelectModel[]) =>
                                onMultipSelectChange(selectedOptions, 'reviewers')
                            }
                        />
                        <small className='fw-lighter'>
                            {t('Assessment.ReviewersTooltip')}
                        </small>
                    </div>
                </div>

                <div className='mb-3'>
                    {error['userAlreadyAssigned'] && (
                        <p className='text-danger'>{error['userAlreadyAssigned']}</p>
                    )}
                    <ModalFooter>
                        <>
                            <button
                                className='btn app-btn-secondary'
                                onClick={() => onDialogClose(DialogAction.Close)}
                            >
                                {t('Common.Cancel')}
                            </button>
                            <button
                                className='btn app-btn-primary'
                                onClick={createUpdateAssessment}
                            >
                                {!assessmentId
                                    ? t('Assessment.AddAssessment')
                                    : t('Common.Update')}
                            </button>
                        </>
                    </ModalFooter>
                </div>
            </div>
        </>
    );
};

export default AddAssessment;